import { useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EVENTS_FOR_TARGET, EVENT_LABELS, EVENT_HELP } from "@/lib/notifications";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import api from "@/lib/api";

/**
 * Bell button + popover of per-follow alert toggles, with a one-tap "Mute" switch.
 * Works for any follow target (user/team/trainer/event/trainer_listing).
 * Pass `initialPrefs` to skip the fetch (e.g. from the Following list); otherwise
 * the current prefs are fetched from the server the first time it's opened.
 */
export default function FollowAlertsBell({ targetType, targetId, initialPrefs = null, muteLabel, size = 20, align = "end", triggerClassName, triggerStyle }) {
    const { setFollowPrefs } = useAuth();
    const ACCENT = { team: "#ff4500", trainer: "#2196f3", trainer_listing: "#2196f3", event: "#22c55e", user: "#a855f7" };
    const accent = ACCENT[targetType] || "#ff4500";
    const [prefs, setPrefs] = useState(initialPrefs || {});
    const [loaded, setLoaded] = useState(initialPrefs != null);
    const [saving, setSaving] = useState(false);
    const events = EVENTS_FOR_TARGET[targetType] || [];
    // Unset defaults to ON (matches the server's DEFAULT_DEFAULTS).
    const effective = (t) => (t in prefs ? !!prefs[t] : true);
    const muted = events.length > 0 && events.every((t) => !effective(t));

    const onOpenChange = async (open) => {
        if (open && !loaded) {
            try {
                const { data } = await api.get(`/follows/${targetType}/${targetId}/prefs`);
                setPrefs(data.prefs || {});
            } catch { /* ignore */ }
            setLoaded(true);
        }
    };

    const save = async (next) => {
        setPrefs(next);
        setSaving(true);
        const res = await setFollowPrefs(targetType, targetId, next);
        setSaving(false);
        if (!res.ok) toast.error(res.error || "Could not save alert setting");
    };

    const toggle = (t) => save({ ...prefs, [t]: !effective(t) });
    const toggleMute = () => {
        const next = {};
        events.forEach((t) => { next[t] = muted; }); // muted→turn all ON, else turn all OFF
        save(next);
        toast.success(muted ? "Alerts on" : "Muted — you won't get alerts");
    };

    return (
        <Popover onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button type="button" title="Alert preferences" data-testid={`follow-bell-${targetId}`}
                    style={triggerStyle !== undefined ? triggerStyle : (!muted ? { color: accent } : undefined)}
                    className={triggerClassName || `inline-flex items-center justify-center h-9 w-9 border border-[#2a2a2a] bg-[#0f0f0f] transition-colors shrink-0 ${muted ? "text-[#666] hover:text-[#a0a0a0]" : ""}`}>
                    {muted ? <BellOff style={{ width: size, height: size }} /> : <Bell style={{ width: size, height: size }} />}
                </button>
            </PopoverTrigger>
            <PopoverContent align={align} className="w-72 bg-[#0f0f0f] border-[#2a2a2a] rounded-none p-3" data-testid={`follow-bell-panel-${targetId}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Alert me about {saving ? "· saving…" : ""}</div>
                <div className="space-y-2">
                    {events.map((t) => (
                        <label key={t} className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid={`follow-pref-${targetId}-${t}`}>
                            <input type="checkbox" checked={effective(t)} onChange={() => toggle(t)} style={{ accentColor: accent }} className="mt-0.5 h-4 w-4" />
                            <span className="text-sm text-white leading-tight">
                                {EVENT_LABELS[t]}
                                <span className="block text-[11px] text-[#a0a0a0]">{EVENT_HELP[t]}</span>
                            </span>
                        </label>
                    ))}
                </div>
                <div className="mt-2 pt-2 border-t border-[#2a2a2a] flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-sm text-white"><BellOff className="h-4 w-4 text-[#a0a0a0]" /> {muteLabel || "Mute"}</span>
                    <button type="button" role="switch" aria-checked={muted} onClick={toggleMute} data-testid={`follow-mute-${targetId}`}
                        style={muted ? { backgroundColor: accent } : undefined}
                        className={`relative h-5 w-9 rounded-full transition-colors ${muted ? "" : "bg-[#333]"}`}>
                        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${muted ? "left-[18px]" : "left-0.5"}`} />
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
